import {
  type EventCatalogEntry,
  catalogForChapter,
} from "@repo/vihangam-yog-shared";

import { OCTOBER, octoberEvents } from "../../content/october";
import { langFromPath, localePath } from "../../i18n/lang";
import { Agenda } from "../../ui/Agenda";
import { Button } from "../../ui/Button";
import { Callout } from "../../ui/Callout";
import { EventWhere } from "../../ui/EventWhere";
import { KundCounter } from "../register/KundCounter";
import { priceLines } from "../register/kunds";

/** The price lines for a day: one per priced tier in the organisers'
 *  wording ("S$51 per person (shared Kund)"), or the free-entry line. */
function dayPriceLines(e: EventCatalogEntry): string[] {
  if (!e.price) {
    return ["Free entry (with QR code), registration required"];
  }
  const lines = priceLines(e);
  return lines.length > 0 ? lines : ["Registration and payment on this site"];
}

/** The organisers' one-page flyer for the visit, shown once above the days. */
function VisitFlyer() {
  return (
    <div className="grid gap-5 rounded-2xl border border-hairline bg-card p-5 shadow-soft md:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] md:items-center md:p-6">
      <img
        src={OCTOBER.visitFlyer.src}
        alt={OCTOBER.visitFlyer.alt}
        width={858}
        height={1820}
        loading="lazy"
        className="mx-auto w-full max-w-[22rem] rounded-lg object-contain"
      />
      <p className="max-w-prose text-ink-soft md:text-lg">
        {OCTOBER.visit.datesLabel} {OCTOBER.visit.otherProgrammes}
      </p>
    </div>
  );
}

function Day({
  e,
  poster,
  posterAlt,
  notices,
  kunds,
  objectives,
}: {
  e: EventCatalogEntry;
  poster: string | null;
  posterAlt?: string;
  /** Practical lines for the day, highlighted with the price. */
  notices: string[];
  kunds?: boolean;
  /** The organisers' stated aims for the day, shown under the price. */
  objectives?: string[];
}) {
  return (
    <article
      className={`grid gap-5 rounded-2xl border border-hairline bg-card p-6 shadow-soft md:p-8 ${poster ? "md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]" : ""}`}
    >
      {poster ? (
        <img
          src={poster}
          alt={posterAlt ?? `${e.title} poster`}
          className="w-full rounded-lg object-cover"
        />
      ) : null}
      <div className="flex flex-col">
        <p className="eyebrow">{e.dateLabel}</p>
        <h3 className="font-display mt-2 text-2xl text-ink md:text-3xl">
          {e.title}
        </h3>
        {e.timeLabel ? <p className="mt-2 text-ink">{e.timeLabel}</p> : null}
        <EventWhere event={e} className="mt-1" />
        <Callout className="mt-4">
          {dayPriceLines(e).map((line) => (
            <p key={line} className="font-semibold">
              {line}
            </p>
          ))}
          {notices.map((line) => (
            <p key={line}>{line}</p>
          ))}
          {e.arriveByLabel ? <p>Arrive by {e.arriveByLabel}</p> : null}
        </Callout>
        {kunds ? <KundCounter event={e} /> : null}
        <Agenda rows={e.agenda} className="mt-4" />
        {objectives && objectives.length > 0 ? (
          <div className="mt-5">
            <p className="eyebrow">Objectives of Vedic Hawan</p>
            <ol className="mt-2 grid list-decimal gap-1 pl-5 text-ink-soft">
              {objectives.map((o) => (
                <li key={o}>{o}</li>
              ))}
            </ol>
          </div>
        ) : null}
        <div className="mt-auto pt-5">
          <Button href="/register" size="lg">
            Register
          </Button>
        </div>
      </div>
    </article>
  );
}

/** The invitation-only dialogues, first because they come first in the
 *  week. No Register button: people come to these when the organisers
 *  invite them. */
function InvitationOnly() {
  if (OCTOBER.invitationOnly.length === 0) {
    return null;
  }
  return (
    <section
      className="grid gap-4"
      data-testid="invitation-only"
      aria-labelledby="invitation-only-heading"
    >
      <div>
        <p className="eyebrow">1 and 2 October</p>
        <h3
          id="invitation-only-heading"
          className="font-display mt-2 text-2xl text-ink md:text-3xl"
        >
          By invitation only
        </h3>
        <p className="mt-2 max-w-prose text-ink-soft">
          Two evening dialogues open the week. Participation is only by
          invitation from the organising team, so there is nothing to register
          for here.
        </p>
      </div>
      <ul className="grid gap-4 md:grid-cols-2">
        {OCTOBER.invitationOnly.map((p) => (
          <li
            key={p.title}
            className="grid gap-3 rounded-2xl border border-hairline bg-card p-6 shadow-soft"
          >
            <p className="eyebrow">{p.dateLabel}</p>
            <p className="font-display text-xl text-ink md:text-2xl">
              {p.title}
            </p>
            <p className="text-ink">{p.timeLabel}</p>
            <p className="text-sm text-ink-soft">
              {p.venue}, {p.address}{" "}
              <a
                href={p.mapUrl}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-teal-deep underline underline-offset-4"
              >
                Map
              </a>
            </p>
            <Callout>
              <p className="font-semibold">Participation only by invitation</p>
            </Callout>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The recurring Zoom sessions. Separate from the visit, so the pages
 *  place it lower down on its own. */
export function WeeklySessions() {
  const recurring = catalogForChapter("sg").filter((e) => !e.startsAt);
  const { lang } = langFromPath(window.location.pathname);
  if (recurring.length === 0) {
    return null;
  }
  return (
    <ul className="grid gap-3 sm:grid-cols-2" data-testid="weekly-sessions">
      {recurring.map((e) => (
        <li
          key={e.slug}
          className="rounded-lg border border-hairline bg-ivory-deep p-4"
        >
          <p className="font-display text-lg text-ink">{e.title}</p>
          <p className="text-sm text-ink-soft">
            {e.dateLabel} · {e.timeLabel}
          </p>
          <a
            href={localePath("/activities/daily-practice", lang)}
            className="mt-2 inline-block text-sm font-medium text-teal-deep underline-offset-4 hover:underline"
          >
            Join on Zoom
          </a>
        </li>
      ))}
    </ul>
  );
}

interface UpcomingBlockProps {
  /** The events page shows the visit flyer and the live Kund count; the
   *  home page keeps the block light and network-free. */
  full?: boolean;
}

/** The visit as one story: the occasion, the two invitation-only
 *  dialogues, then the two open days with their one registration. */
export function UpcomingBlock({ full = false }: UpcomingBlockProps) {
  const [stage, hawan] = octoberEvents();
  return (
    <div className="grid gap-10">
      <p className="max-w-prose text-lg leading-relaxed text-ink md:text-xl">
        {OCTOBER.visit.occasion}
      </p>
      {full ? <VisitFlyer /> : null}
      <InvitationOnly />
      <section
        className="grid gap-4"
        data-testid="open-days"
        aria-labelledby="open-days-heading"
      >
        <div>
          <p className="eyebrow">3 and 4 October</p>
          <h3
            id="open-days-heading"
            className="font-display mt-2 text-2xl text-ink md:text-3xl"
          >
            Open to all, register once
          </h3>
          <p className="mt-2 max-w-prose text-ink-soft">
            One registration covers both days. Bring your family and friends;
            list everyone who is coming.
          </p>
        </div>
        <div className="grid gap-6">
          {stage ? (
            <Day
              e={stage}
              poster={OCTOBER.stage.poster}
              notices={OCTOBER.stage.notices}
            />
          ) : null}
          {hawan ? (
            <Day
              e={hawan}
              poster={OCTOBER.hawan.poster}
              posterAlt={OCTOBER.hawan.posterAlt}
              notices={OCTOBER.hawan.notices}
              kunds={full}
              objectives={OCTOBER.hawan.objectives}
            />
          ) : null}
        </div>
      </section>
    </div>
  );
}
